import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Chip,
    Box
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, orderBy, onSnapshot, getDocs, Timestamp } from 'firebase/firestore';
import PhotoThumbnail from '../components/PhotoThumbnail';
import VisitorCheckoutQR from '../components/VisitorCheckoutQR';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloseIcon from '@mui/icons-material/Close';

const HostVisitHistory = ({ operatorTenantId }) => {
    const { user, isSuperAdmin, isAppAdmin } = useAuth();
    const navigate = useNavigate();
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [qrDialog, setQrDialog] = useState({ open: false, visit: null });

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        // Restrict superadmin from accessing visit history
        if (isSuperAdmin()) {
            setVisits([]);
            setLoading(false);
            return;
        }


        const fetchVisitHistory = async () => {
            try {
                let q;
                const now = new Date();
                // Host: show all visits for the last 30 days
                if (user.role === 'host') {
                    const thirtyDaysAgo = new Date(now);
                    thirtyDaysAgo.setDate(now.getDate() - 30);
                    const thirtyDaysAgoTimestamp = Timestamp.fromDate(thirtyDaysAgo);
                    q = query(
                        collection(db, 'visit_approvals'),
                        where('createdAt', '>=', thirtyDaysAgoTimestamp),
                        orderBy('createdAt', 'desc')
                    );
                    // Operator: show only visits created by operator in the last 1 day
                } else if (user.role === 'operator') {
                    const oneDayAgo = new Date(now);
                    oneDayAgo.setDate(now.getDate() - 1);
                    const oneDayAgoTimestamp = Timestamp.fromDate(oneDayAgo);
                    q = query(
                        collection(db, 'visit_approvals'),
                        where('createdBy', '==', user.uid),
                        where('createdAt', '>=', oneDayAgoTimestamp),
                        orderBy('createdAt', 'desc')
                    );
                    // AppAdmin/AppOperator: keep existing logic (show all for their hosts, no time filter)
                } else if (user.role === 'appoperator' && operatorTenantId) {
                    const hostsQuery = query(
                        collection(db, "users"),
                        where("tenantId", "==", operatorTenantId),
                        where("role", "==", "host")
                    );
                    const hostsSnapshot = await getDocs(hostsQuery);
                    const hostIds = hostsSnapshot.docs.map(doc => doc.id);
                    if (hostIds.length === 0) {
                        setVisits([]);
                        setLoading(false);
                        return;
                    }
                    q = query(
                        collection(db, 'visit_approvals'),
                        where('hostUserID', 'in', hostIds),
                        orderBy('createdAt', 'desc')
                    );
                } else if (isAppAdmin()) {
                    const tenantQuery = query(
                        collection(db, "tenantmaster"),
                        where("appAdminEmail", "==", (user.userEmailID || user.email).toLowerCase())
                    );
                    const tenantSnapshot = await getDocs(tenantQuery);
                    if (!tenantSnapshot.empty) {
                        const tenantData = tenantSnapshot.docs[0].data();
                        if (!tenantData.tenantId) {
                            setVisits([]);
                            setLoading(false);
                            return;
                        }
                        const hostsQuery = query(
                            collection(db, "users"),
                            where("tenantId", "==", tenantData.tenantId),
                            where("role", "==", "host")
                        );
                        const hostsSnapshot = await getDocs(hostsQuery);
                        const hostIds = hostsSnapshot.docs.map(doc => doc.id);
                        if (hostIds.length > 0) {
                            q = query(
                                collection(db, 'visit_approvals'),
                                where('hostUserID', 'in', hostIds),
                                orderBy('createdAt', 'desc')
                            );
                        } else {
                            // No hosts, so show all visits for this tenant
                            q = query(
                                collection(db, 'visit_approvals'),
                                where('tenantId', '==', tenantData.tenantId),
                                orderBy('createdAt', 'desc')
                            );
                        }
                    } else {
                        setVisits([]);
                        setLoading(false);
                        return;
                    }
                } else {
                    setVisits([]);
                    setLoading(false);
                    return;
                }

                const unsubscribe = onSnapshot(q, (snapshot) => {
                    const visitData = snapshot.docs.map(doc => {
                        const data = doc.data();

                        return {
                            id: doc.id,
                            ...data,

                            createdAt: data.createdAt?.toDate
                                ? data.createdAt.toDate()
                                : data.createdAt
                                    ? new Date(data.createdAt)
                                    : null,

                            visitorTime: data.visitorTime?.toDate
                                ? data.visitorTime.toDate()
                                : data.visitorTime
                                    ? new Date(data.visitorTime)
                                    : null
                        };
                    });
                    console.log('[AppOperator VisitHistory] visits fetched:', visitData.length, visitData);
                    setVisits(visitData);
                    setLoading(false);
                }, (error) => {
                    // Only suppress Firestore index errors if visits are loaded
                    if (error && error.code === 'failed-precondition' && error.message && error.message.includes('index')) {
                        // Do not log index errors if visits are loaded
                        setLoading(false);
                    } else {
                        console.error("Error fetching visit history:", error);
                        setLoading(false);
                    }
                });

                return () => unsubscribe();
            } catch (error) {
                console.error("Error setting up visit history query:", error);
                setLoading(false);
            }
        };

        fetchVisitHistory();
    }, [user, navigate, isSuperAdmin, isAppAdmin, operatorTenantId]);

    const formatDateTime = (date) => {
        if (!date) return 'N/A';
        return new Intl.DateTimeFormat('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short'
        }).format(date);
    };

    return (
        <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>
            <Container maxWidth="lg">

                {/* HEADER */}
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{
                        fontSize: "2rem",
                        fontWeight: 900,
                        fontFamily: "'Unbounded', sans-serif",
                        color: "#1c1400"
                    }}>
                        Visit History
                    </Typography>

                    <Typography sx={{ color: "#8a7a40" }}>
                        Track all visitor activities and approvals
                    </Typography>
                </Box>

                {/* LOADING */}
                {loading ? (
                    <Box sx={{ textAlign: "center", mt: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : visits.length === 0 ? (

                    /* EMPTY STATE */
                    <Paper sx={{
                        p: 4,
                        textAlign: "center",
                        borderRadius: "16px",
                        border: "1px solid #f0d060",
                        background: "#fffbea"
                    }}>
                        <Typography sx={{ color: "#6b7280" }}>
                            No visit history found.
                        </Typography>
                    </Paper>

                ) : (

                    /* TABLE CARD */
                    <Paper sx={{
                        borderRadius: "16px",
                        border: "1px solid #f0d060",
                        overflow: "hidden",
                        background: "#ffffff"
                    }}>

                        <TableContainer>

                            <Table>

                                {/* HEADER */}
                                <TableHead>
                                    <TableRow sx={{ background: "#fef3c7" }}>
                                        <TableCell><b>Photo</b></TableCell>
                                        <TableCell><b>Name</b></TableCell>
                                        <TableCell><b>Email</b></TableCell>
                                        <TableCell><b>Reason</b></TableCell>
                                        <TableCell><b>Scheduled</b></TableCell>
                                        <TableCell><b>Status</b></TableCell>
                                        <TableCell><b>Created</b></TableCell>
                                        <TableCell><b>QR</b></TableCell>
                                    </TableRow>
                                </TableHead>

                                {/* BODY */}
                                <TableBody>
                                    {visits.map((visit) => (
                                        <TableRow
                                            key={visit.id}
                                            sx={{
                                                transition: "0.2s",
                                                "&:hover": {
                                                    background: "#fffbea"
                                                }
                                            }}
                                        >

                                            {/* PHOTO */}
                                            <TableCell>
                                                <PhotoThumbnail
                                                    photoUrl={visit.photoUrl}
                                                    visitorName={visit.visitorName}
                                                    visitorUserID={visit.visitorUserID}
                                                    visitorEmail={visit.visitorEmail}
                                                    size={40}
                                                    variant="circular"
                                                    showZoom
                                                    showCamera={false}
                                                />
                                            </TableCell>

                                            {/* NAME */}
                                            <TableCell>
                                                <Typography sx={{ fontWeight: 600 }}>
                                                    {visit.visitorName}
                                                </Typography>
                                            </TableCell>

                                            {/* EMAIL */}
                                            <TableCell sx={{ color: "#6b7280" }}>
                                                {visit.visitorEmail}
                                            </TableCell>

                                            {/* REASON */}
                                            <TableCell sx={{ maxWidth: 180 }}>
                                                <Typography sx={{
                                                    fontSize: "0.9rem",
                                                    color: "#444"
                                                }}>
                                                    {visit.visitReason}
                                                </Typography>
                                            </TableCell>

                                            {/* TIME */}
                                            <TableCell>
                                                {formatDateTime(visit.visitorTime)}
                                            </TableCell>

                                            {/* STATUS */}
                                            <TableCell>
                                                <Chip
                                                    label={visit.status}
                                                    sx={{
                                                        fontWeight: 600,
                                                        textTransform: "capitalize",
                                                        background:
                                                            visit.status === "approved" ? "#dcfce7" :   // green
                                                                visit.status === "rejected" ? "#fee2e2" :   // red
                                                                    visit.status === "pending" ? "#fef3c7" :    // yellow
                                                                        visit.status === "concluded" ? "#e0f2fe" :  // blue (clean choice)
                                                                            "#f3f4f6",
                                                        color:
                                                            visit.status === "approved" ? "#166534" :
                                                                visit.status === "rejected" ? "#991b1b" :
                                                                    visit.status === "pending" ? "#92400e" :
                                                                        visit.status === "concluded" ? "#075985" :
                                                                            "#374151"
                                                    }}
                                                />
                                            </TableCell>

                                            {/* CREATED */}
                                            <TableCell sx={{ color: "#6b7280" }}>
                                                {formatDateTime(visit.createdAt)}
                                            </TableCell>

                                            {/* QR BUTTON */}
                                            <TableCell>
                                                {visit.status === "approved" ? (
                                                    <IconButton
                                                        onClick={() => setQrDialog({ open: true, visit })}
                                                        sx={{
                                                            background: "#1c1400",
                                                            color: "#fde047",
                                                            "&:hover": {
                                                                background: "#f59e0b",
                                                                color: "#1c1400"
                                                            }
                                                        }}
                                                    >
                                                        <QrCodeIcon />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton disabled>
                                                        <QrCodeIcon />
                                                    </IconButton>
                                                )}
                                            </TableCell>

                                        </TableRow>
                                    ))}
                                </TableBody>

                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* QR DIALOG */}
                <Dialog
                    open={qrDialog.open}
                    onClose={() => setQrDialog({ open: false, visit: null })}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: "16px",
                            position: "relative"
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 700 }}>
                        Visitor Checkout QR
                    </DialogTitle>

                    <IconButton
                        onClick={() => setQrDialog({ open: false, visit: null })}
                        sx={{
                            position: "absolute",
                            right: 12,
                            top: 12,
                            color: "#555",
                            "&:hover": {
                                color: "#000"
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <DialogContent>
                        {qrDialog.visit && (
                            <VisitorCheckoutQR
                                visitorData={{
                                    visitorUserID: qrDialog.visit.visitorUserID,
                                    visitorName: qrDialog.visit.visitorName,
                                    visitorEmail: qrDialog.visit.visitorEmail
                                }}
                                visitData={qrDialog.visit}
                            />
                        )}
                    </DialogContent>
                </Dialog>

            </Container>
        </Box>
    );
};

export default HostVisitHistory;